import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import {
    Alert, AlertIcon, Box, Button, ButtonGroup, Divider, Flex,
    HStack, List, ListItem, ModalBody, ModalFooter, SimpleGrid, Stack, Switch, Text,
    useColorModeValue, useDisclosure, useToast
} from "@chakra-ui/react"
import { Form, Icon, Modal } from "components"
import { API, Mask } from "config"
import { Authenticate } from "screens"

export default function CreateDistributor({ onSuccess = () => {}, ...props }) {
    const toast = useToast()
    const navigate = useNavigate()
    const auth = useDisclosure()

    const [isConsultant, setIsConsultant] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [selectedConsultant, setSelectedConsultant] = useState(null)
    const [existingDistributor, setExistingDistributor] = useState(null)
    const [form, setForm] = useState({})
    const [cpfWarning, setCpfWarning] = useState(null)
    const [cnpjWarning, setCnpjWarning] = useState(null)
    const [emailWarning, setEmailWarning] = useState(null)
    const [mobileWarning, setMobileWarning] = useState(null)
    const [mobileConfirmed, setMobileConfirmed] = useState(false)
    const [errors, setErrors] = useState(null)
    const [loading, setLoading] = useState(false)

    const debounceRef = useRef(null)
    const cpfCheckRef = useRef(null)
    const cnpjCheckRef = useRef(null)
    const emailCheckRef = useRef(null)
    const mobileCheckRef = useRef(null)
    const skipNextSearch = useRef(false)
    const searchGenRef = useRef(0)

    const dropdownBg     = useColorModeValue('white', 'gray.800')
    const dropdownBorder = useColorModeValue('gray.200', 'gray.600')
    const dropdownHover  = useColorModeValue('orange.50', 'orange.900')
    const dropdownName   = useColorModeValue('gray.800', 'white')
    const dropdownMeta   = useColorModeValue('gray.500', 'gray.300')
    const checkBg        = useColorModeValue('gray.50', 'gray.700')
    const checkBorder    = useColorModeValue('gray.200', 'gray.600')

    // Limpa tudo ao fechar o modal
    useEffect(() => {
        if (!props.isOpen) {
            clearTimeout(debounceRef.current)
            clearTimeout(cpfCheckRef.current)
            clearTimeout(cnpjCheckRef.current)
            clearTimeout(emailCheckRef.current)
            clearTimeout(mobileCheckRef.current)
            setIsConsultant(true)
            setSearchQuery('')
            setSearchResults([])
            setSearchLoading(false)
            setSelectedConsultant(null)
            setExistingDistributor(null)
            setForm({})
            setCpfWarning(null)
            setCnpjWarning(null)
            setEmailWarning(null)
            setMobileWarning(null)
            setMobileConfirmed(false)
            setErrors(null)
        }
    }, [props.isOpen])

    // Busca de consultores com debounce — skipNextSearch evita reabrir o dropdown após seleção
    useEffect(() => {
        if (!searchQuery || searchQuery.length < 2) {
            setSearchResults([])
            return
        }
        if (skipNextSearch.current) {
            skipNextSearch.current = false
            return
        }
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
            const gen = ++searchGenRef.current
            setSearchLoading(true)
            try {
                const res = await API.get('/search', { params: { term: searchQuery } })
                if (gen === searchGenRef.current) setSearchResults(res?.data?.consultants ?? [])
            } catch {}
            if (gen === searchGenRef.current) setSearchLoading(false)
        }, 350)
        return () => clearTimeout(debounceRef.current)
    }, [searchQuery])

    const handleSelectConsultant = (c) => {
        clearTimeout(debounceRef.current)
        skipNextSearch.current = true
        searchGenRef.current++
        setSearchResults([])
        setSearchQuery(c.customer?.name ?? '')

        if (c.customer?.distributor) {
            setExistingDistributor(c.customer.distributor)
            setSelectedConsultant(null)
            setForm({})
            return
        }

        setExistingDistributor(null)
        setSelectedConsultant(c)
        setForm(f => ({
            ...f,
            customer_id: c.customer_id,
            email:       c.customer?.email ?? '',
            mobile:      Mask.mobile(c.customer?.mobile ?? ''),
            work_number: Mask.mobile(c.customer?.work_number ?? ''),
        }))
    }

    const checkCpf = (maskedCpf) => {
        clearTimeout(cpfCheckRef.current)
        const digits = (maskedCpf ?? '').replace(/\D/g, '')
        if (digits.length < 11) { setCpfWarning(null); return }
        cpfCheckRef.current = setTimeout(async () => {
            try {
                const res = await API.get('/search', { params: { term: digits } })
                const found = res?.data?.consultants?.[0]
                if (!found) { setCpfWarning(null); return }
                if (found.customer?.distributor) {
                    setCpfWarning(`CPF pertence ao consultor(a) ${found.customer?.name}, que já possui um núcleo cadastrado. O responsável já é consultor(a)?`)
                } else {
                    setCpfWarning(`CPF já cadastrado como consultor(a): ${found.customer?.name}. O responsável já é consultor(a)?`)
                }
            } catch {
                setCpfWarning(null)
            }
        }, 500)
    }

    const checkCnpj = (maskedCnpj) => {
        clearTimeout(cnpjCheckRef.current)
        const digits = (maskedCnpj ?? '').replace(/\D/g, '')
        if (digits.length < 14) { setCnpjWarning(null); return }
        cnpjCheckRef.current = setTimeout(async () => {
            try {
                const res = await API.get('/distributors/list', { params: { term: digits } })
                const found = res?.data?.data?.find(d => String(d.doc_cnpj) === digits)
                setCnpjWarning(found ? `CNPJ já cadastrado no núcleo: ${found.name ?? found.company_name}.` : null)
            } catch {
                setCnpjWarning(null)
            }
        }, 500)
    }

    const checkDistributorEmail = (email) => {
        clearTimeout(emailCheckRef.current)
        if (!email || !email.includes('@')) { setEmailWarning(null); return }
        emailCheckRef.current = setTimeout(async () => {
            try {
                const res = await API.get('/distributors/list', { params: { term: email } })
                const found = res?.data?.data?.find(d => d.email?.toLowerCase() === email.toLowerCase())
                setEmailWarning(found ? `E-mail já cadastrado no núcleo: ${found.name}.` : null)
            } catch {
                setEmailWarning(null)
            }
        }, 500)
    }

    const checkMobile = (maskedPhone) => {
        clearTimeout(mobileCheckRef.current)
        setMobileConfirmed(false)
        const digits = (maskedPhone ?? '').replace(/\D/g, '')
        if (digits.length < 10) { setMobileWarning(null); return }
        mobileCheckRef.current = setTimeout(async () => {
            try {
                const [resSearch, resDist] = await Promise.all([
                    API.get('/search', { params: { term: digits } }),
                    API.get('/distributors/list', { params: { term: digits } }),
                ])
                const consultant = resSearch?.data?.consultants?.[0]
                if (consultant) {
                    setMobileWarning({ name: consultant.customer?.name ?? 'desconhecido', source: 'consultant' })
                    return
                }
                const distributor = resDist?.data?.data?.find(d =>
                    String(d.mobile ?? '').replace(/\D/g, '') === digits ||
                    String(d.work_number ?? '').replace(/\D/g, '') === digits
                )
                if (distributor) {
                    setMobileWarning({ name: distributor.name ?? distributor.company_name ?? 'desconhecido', source: 'distributor' })
                    return
                }
                setMobileWarning(null)
            } catch {
                setMobileWarning(null)
            }
        }, 500)
    }

    const onChange = ({ target }) => {
        const { name, value } = target
        switch (name) {
            case 'customer_doc_cpf': {
                const masked = value ? Mask.cpf(value) : ''
                setForm({ ...form, [name]: masked })
                checkCpf(masked)
                break
            }
            case 'doc_cnpj': {
                const masked = Mask.cnpj(value)
                setForm({ ...form, [name]: masked })
                checkCnpj(masked)
                break
            }
            case 'customer_email': {
                setForm({ ...form, [name]: value })
                checkDistributorEmail(value)
                break
            }
            case 'zip':             setForm({ ...form, [name]: Mask.zip(value) }); break
            case 'dob':             setForm({ ...form, [name]: Mask.date(value) }); break
            case 'mobile':
            case 'work_number': setForm({ ...form, [name]: Mask.mobile(value) }); break
            case 'customer_mobile': {
                const masked = Mask.mobile(value)
                setForm({ ...form, [name]: masked })
                checkMobile(masked)
                break
            }
            default:                setForm({ ...form, [name]: value }); break
        }
    }

    const searchCNPJ = async (doc_cnpj) => {
        setLoading(true)
        try {
            const res = await API.get(`/ext/cnpj/${doc_cnpj.replace(/\D/g, '')}`)
            setForm(f => ({
                ...f,
                company_name:         res.data?.['RAZAO SOCIAL'],
                dob:                  res.data?.['DATA ABERTURA'],
                address_line_1:       `${res.data?.['TIPO LOGRADOURO'] ?? ''} ${res.data?.['LOGRADOURO'] ?? ''}`.trim(),
                address_complement:   res.data?.['COMPLEMENTO'],
                address_number:       res.data?.['NUMERO'],
                address_neighborhood: res.data?.['BAIRRO'],
                zip:                  res.data?.['CEP'] ? Mask.zip(res.data['CEP']) : f.zip,
                work_number:          res.data?.['DDD'] ? Mask.mobile(`${res.data['DDD']}${res.data['TELEFONE'] ?? ''}`) : f.work_number,
                email:                res.data?.['EMAIL'] || f.email,
                doc_cnpj,
            }))
            toast({ description: 'Dados preenchidos pelo CNPJ.', status: 'success', position: 'bottom-right' })
        } catch {
            toast({ description: 'CNPJ não encontrado.', position: 'bottom-right' })
        }
        setLoading(false)
    }

    useEffect(() => {
        if ((form?.doc_cnpj?.length ?? 0) >= 18) searchCNPJ(form.doc_cnpj)
    }, [form?.doc_cnpj])

    const handleSubmit = async () => {
        setLoading(true)
        setErrors(null)
        try {
            const payload = {
                ...form,
                mobile:           (form.mobile ?? '').replace(/\D/g, ''),
                work_number:      (form.work_number ?? '').replace(/\D/g, ''),
                customer_mobile:  (form.customer_mobile ?? '').replace(/\D/g, ''),
                customer_doc_cpf: (form.customer_doc_cpf ?? '').replace(/\D/g, '') || undefined,
                doc_cnpj:         (form.doc_cnpj ?? '').replace(/\D/g, ''),
                // Regra de negócio: email do distributor = email do customer quando criando novo responsável
                ...(!isConsultant && { email: form.customer_email }),
            }
            const res = await API.post('/distributors', payload)
            toast({ title: 'Núcleo registrado com sucesso.', status: 'success' })
            props.onClose()
            onSuccess()
            navigate(`/distributors/${res?.data?.id}`)
        } catch (err) {
            setErrors(err?.response?.data?.errors)
            toast({ title: 'Não foi possível criar o núcleo.', description: err?.response?.data?.message, status: 'warning' })
        }
        setLoading(false)
    }

    const resetToggle = () => {
        setIsConsultant(v => !v)
        setSelectedConsultant(null)
        setExistingDistributor(null)
        setSearchQuery('')
        setCpfWarning(null)
        setCnpjWarning(null)
        setEmailWarning(null)
        setMobileWarning(null)
        setMobileConfirmed(false)
        setForm({})
    }

    // Flag ON: só mostra após consultor válido selecionado
    // Flag OFF: mostra tudo de imediato
    const showNucleusForm = isConsultant ? (!!selectedConsultant && !existingDistributor) : true

    return (
        <>
        <Modal loading={loading} size="lg" {...props}>
            <ModalBody>
                <Stack spacing={6}>
                    <Flex direction="column" alignItems="center">
                        <Icon name="distributor" w={10} h={10} />
                        <Text fontSize="lg" mt={2} fontWeight="semibold">Criar núcleo de distribuição</Text>
                    </Flex>

                    {/* Toggle */}
                    <Box
                        p={4}
                        borderRadius="md"
                        borderWidth={1}
                        borderColor="orange.300"
                        borderLeftWidth={4}
                        borderLeftColor="orange.400"
                        bg="blackAlpha.50"
                    >
                        <HStack justifyContent="space-between">
                            <Box>
                                <Text fontWeight="semibold" fontSize="sm">O responsável já é consultor(a)?</Text>
                                <Text fontSize="xs" color="gray.500" mt={0.5}>
                                    {isConsultant
                                        ? 'Vincula o núcleo a uma conta consultora existente.'
                                        : 'Cadastra um novo responsável e cria o núcleo.'}
                                </Text>
                            </Box>
                            <Switch isChecked={isConsultant} onChange={resetToggle} colorScheme="orange" />
                        </HStack>
                    </Box>

                    {/* ── Modo: responsável JÁ É consultor ── */}
                    {isConsultant && (
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={1}>Pesquisar consultor(a):</Text>
                            <Box position="relative">
                                <Form.Text
                                    placeholder="Nome, código, CPF ou e-mail do consultor..."
                                    value={searchQuery}
                                    autoComplete="off"
                                    onChange={e => {
                                        setSearchQuery(e.target.value)
                                        setSelectedConsultant(null)
                                        setExistingDistributor(null)
                                        setForm(f => ({ ...f, customer_id: undefined, email: undefined, mobile: undefined, work_number: undefined }))
                                    }}
                                    onKeyDown={e => { if (e.key === 'Enter') e.preventDefault() }}
                                    isDisabled={loading}
                                    helper={searchLoading ? 'Buscando...' : undefined}
                                />
                                {searchResults.length > 0 && (
                                    <Box
                                        position="absolute"
                                        top="100%"
                                        left={0}
                                        right={0}
                                        bg={dropdownBg}
                                        border="1px solid"
                                        borderColor={dropdownBorder}
                                        borderRadius="md"
                                        boxShadow="lg"
                                        zIndex={10}
                                        maxH="200px"
                                        overflowY="auto"
                                    >
                                        <List>
                                            {searchResults.map(item => (
                                                <ListItem
                                                    key={item.id}
                                                    px={4}
                                                    py={2.5}
                                                    cursor="pointer"
                                                    _hover={{ bg: dropdownHover }}
                                                    onMouseDown={e => e.preventDefault()}
                                                    onClick={() => handleSelectConsultant(item)}
                                                >
                                                    <Text fontSize="sm" fontWeight="medium" color={dropdownName}>{item.customer?.name}</Text>
                                                    <Text fontSize="xs" color={dropdownMeta}>{item.customer?.email} · #{item.cod}</Text>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </Box>

                            {selectedConsultant && !existingDistributor && (
                                <Alert status="success" mt={2} borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold">{selectedConsultant.customer?.name}</Text>
                                        <Text fontSize="xs">{selectedConsultant.customer?.email}</Text>
                                    </Box>
                                </Alert>
                            )}

                            {existingDistributor && (
                                <Alert status="warning" mt={2} borderRadius="md">
                                    <AlertIcon />
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold">Este consultor já possui um núcleo cadastrado</Text>
                                        <Text fontSize="xs" mt={0.5}>
                                            <strong>{existingDistributor.name}</strong>
                                            {existingDistributor.cod ? ` · #${existingDistributor.cod}` : ''}
                                            {existingDistributor.email ? ` · ${existingDistributor.email}` : ''}
                                        </Text>
                                        <Text fontSize="xs" mt={1} color="gray.500">
                                            Para criar um novo núcleo para este consultor, encerre ou transfira o núcleo existente primeiro.
                                        </Text>
                                    </Box>
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* ── Modo: novo responsável (não consultor) ── */}
                    {!isConsultant && (
                        <Stack spacing={4}>
                            {/* Card de verificação de unicidade */}
                            <Box p={4} borderRadius="lg" bg={checkBg} border="1px solid" borderColor={checkBorder}>
                                <HStack mb={3} spacing={2}>
                                    <Icon name="search" boxSize="14px" color="gray.400" />
                                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color="gray.500">
                                        Verificação de dados
                                    </Text>
                                </HStack>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    {/* CPF */}
                                    <Box>
                                        <Form.Text
                                            label="CPF:"
                                            name="customer_doc_cpf"
                                            value={form?.customer_doc_cpf ?? ''}
                                            error={errors?.customer_doc_cpf}
                                            onChange={onChange}
                                            onBlur={() => checkCpf(form?.customer_doc_cpf)}
                                            isDisabled={loading}
                                            placeholder="000.000.000-00"
                                            maxLength={14}
                                        />
                                        {cpfWarning && (
                                            <Alert status="warning" mt={2} borderRadius="md" py={1.5}>
                                                <AlertIcon boxSize="14px" mr={1.5} />
                                                <Text fontSize="xs">{cpfWarning}</Text>
                                            </Alert>
                                        )}
                                    </Box>
                                    {/* E-mail */}
                                    <Box>
                                        <Form.Text
                                            required
                                            label="E-mail:"
                                            name="customer_email"
                                            type="email"
                                            value={form?.customer_email ?? ''}
                                            error={errors?.customer_email}
                                            onChange={onChange}
                                            onBlur={() => checkDistributorEmail(form?.customer_email)}
                                            isDisabled={loading}
                                            placeholder="responsavel@email.com"
                                        />
                                        {emailWarning && (
                                            <Alert status="error" mt={2} borderRadius="md" py={1.5}>
                                                <AlertIcon boxSize="14px" mr={1.5} />
                                                <Text fontSize="xs">{emailWarning}</Text>
                                            </Alert>
                                        )}
                                    </Box>
                                    {/* Celular */}
                                    <Box>
                                        <Form.Text
                                            required
                                            label="Celular/WhatsApp:"
                                            name="customer_mobile"
                                            value={form?.customer_mobile ?? ''}
                                            error={errors?.customer_mobile}
                                            onChange={onChange}
                                            onBlur={() => checkMobile(form?.customer_mobile)}
                                            isDisabled={loading}
                                            placeholder="(00) 00000-0000"
                                        />
                                        {mobileWarning && !mobileConfirmed && (
                                            <Alert status="warning" mt={2} borderRadius="md" py={2} flexDir="column" alignItems="flex-start" gap={1}>
                                                <HStack>
                                                    <AlertIcon boxSize="14px" />
                                                    <Text fontSize="xs">
                                                        Telefone já cadastrado em nome de <strong>{mobileWarning.name}</strong>
                                                        {' '}({mobileWarning.source === 'consultant' ? 'consultor(a)' : 'núcleo'}).
                                                    </Text>
                                                </HStack>
                                                <Button size="xs" ml={6} colorScheme="orange" variant="link" onClick={() => setMobileConfirmed(true)}>
                                                    Usar este telefone mesmo assim
                                                </Button>
                                            </Alert>
                                        )}
                                        {mobileWarning && mobileConfirmed && (
                                            <Alert status="success" mt={2} borderRadius="md" py={1.5}>
                                                <AlertIcon boxSize="14px" mr={1.5} />
                                                <Text fontSize="xs">Telefone compartilhado confirmado.</Text>
                                            </Alert>
                                        )}
                                    </Box>
                                    {/* CNPJ */}
                                    <Box>
                                        <Form.Text
                                            required
                                            label="CNPJ:"
                                            name="doc_cnpj"
                                            value={form?.doc_cnpj ?? ''}
                                            error={errors?.doc_cnpj}
                                            onChange={onChange}
                                            onBlur={() => checkCnpj(form?.doc_cnpj)}
                                            isDisabled={loading}
                                            placeholder="00.000.000/0000-00"
                                            maxLength={18}
                                        />
                                        {cnpjWarning && (
                                            <Alert status="warning" mt={2} borderRadius="md" py={1.5}>
                                                <AlertIcon boxSize="14px" mr={1.5} />
                                                <Text fontSize="xs">{cnpjWarning}</Text>
                                            </Alert>
                                        )}
                                    </Box>
                                </SimpleGrid>
                            </Box>

                            {/* Dados pessoais do responsável */}
                            <Text fontSize="sm" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                                Dados do responsável
                            </Text>

                            <Form.Text
                                required
                                label="Nome completo:"
                                name="customer_name"
                                value={form?.customer_name ?? ''}
                                error={errors?.customer_name}
                                onChange={onChange}
                                isDisabled={loading}
                                placeholder="Ex.: Maria da Silva"
                            />

                            <Form.Password
                                required
                                label="Senha de acesso:"
                                name="customer_password"
                                value={form?.customer_password ?? ''}
                                error={errors?.customer_password}
                                onChange={onChange}
                                isDisabled={loading}
                                placeholder="Mínimo 6 caracteres"
                            />
                        </Stack>
                    )}

                    {/* ── Formulário do núcleo ── */}
                    {showNucleusForm && (
                        <>
                            <Divider />

                            {isConsultant && (
                                <Form.Text
                                    isDisabled
                                    label="Responsável vinculado:"
                                    value={selectedConsultant?.customer?.name}
                                    helper="O núcleo compartilhará a conta deste consultor."
                                />
                            )}

                            <Form.Text
                                required
                                label="Nome do núcleo:"
                                name="name"
                                value={form?.name ?? ''}
                                error={errors?.name}
                                onChange={onChange}
                                isDisabled={loading}
                                placeholder="Ex.: Núcleo Centro"
                                size="lg"
                                fontWeight="bold"
                            />

                            <Divider />

                            {isConsultant && (
                                <>
                                    <Form.Text
                                        required
                                        label="CNPJ:"
                                        name="doc_cnpj"
                                        value={form?.doc_cnpj ?? ''}
                                        error={errors?.doc_cnpj}
                                        onChange={onChange}
                                        onBlur={() => checkCnpj(form?.doc_cnpj)}
                                        isDisabled={loading}
                                        placeholder="00.000.000/0000-00"
                                        maxLength={18}
                                        size="lg"
                                    />

                                    {cnpjWarning && (
                                        <Alert status="warning" borderRadius="md">
                                            <AlertIcon />
                                            <Text fontSize="xs">{cnpjWarning}</Text>
                                        </Alert>
                                    )}
                                </>
                            )}

                            <Form.Text
                                required
                                label="Razão Social:"
                                name="company_name"
                                value={form?.company_name ?? ''}
                                error={errors?.company_name}
                                onChange={onChange}
                                isDisabled={loading}
                            />

                            <Form.Text
                                required
                                label="Data de abertura:"
                                name="dob"
                                value={form?.dob ?? ''}
                                error={errors?.dob}
                                onChange={onChange}
                                isDisabled={loading}
                                placeholder="DD/MM/AAAA"
                                maxLength={10}
                            />

                            <Form.Text
                                required
                                label="Inscrição Estadual (IE):"
                                name="doc_ie"
                                value={form?.doc_ie ?? ''}
                                error={errors?.doc_ie}
                                onChange={onChange}
                                isDisabled={loading}
                                helper="Necessário para gerar notas fiscais."
                            />

                            <Divider />

                            {isConsultant && (
                                <Form.Text
                                    required
                                    label="E-mail do núcleo:"
                                    name="email"
                                    value={form?.email ?? ''}
                                    error={errors?.email}
                                    onChange={onChange}
                                    isDisabled={loading || !!selectedConsultant}
                                    helper="Herdado do cadastro do consultor."
                                />
                            )}

                            <HStack>
                                <Form.Text
                                    required
                                    label="Celular/WhatsApp:"
                                    name="mobile"
                                    value={form?.mobile ?? ''}
                                    error={errors?.mobile}
                                    onChange={onChange}
                                    isDisabled={loading}
                                    placeholder="(00) 00000-0000"
                                />
                                <Form.Text
                                    required
                                    label="Tel. de atendimento:"
                                    name="work_number"
                                    value={form?.work_number ?? ''}
                                    error={errors?.work_number}
                                    onChange={onChange}
                                    isDisabled={loading}
                                    placeholder="(00) 00000-0000"
                                    helper="Este número será exibido publicamente para os consultores."
                                />
                            </HStack>

                            <Divider />

                            <Form.Loc onChangeCity={city_id => setForm(f => ({ ...f, city_id }))} isDisabled={loading} />

                            <Form.Text
                                required
                                label="Endereço (Linha 1):"
                                name="address_line_1"
                                value={form?.address_line_1 ?? ''}
                                error={errors?.address_line_1}
                                onChange={onChange}
                                isDisabled={loading}
                            />

                            <Form.Text
                                label="Endereço (Linha 2):"
                                name="address_line_2"
                                value={form?.address_line_2 ?? ''}
                                error={errors?.address_line_2}
                                onChange={onChange}
                                isDisabled={loading}
                            />

                            <HStack>
                                <Form.Text
                                    required
                                    label="Bairro/Setor:"
                                    name="address_neighborhood"
                                    value={form?.address_neighborhood ?? ''}
                                    error={errors?.address_neighborhood}
                                    onChange={onChange}
                                    isDisabled={loading}
                                />
                                <Form.Text
                                    required
                                    label="CEP:"
                                    name="zip"
                                    value={form?.zip ?? ''}
                                    error={errors?.zip}
                                    onChange={onChange}
                                    isDisabled={loading}
                                    maxLength={9}
                                />
                            </HStack>

                            <HStack>
                                <Form.Text
                                    required
                                    label="Número:"
                                    name="address_number"
                                    value={form?.address_number ?? ''}
                                    error={errors?.address_number}
                                    onChange={onChange}
                                    isDisabled={loading}
                                />
                                <Form.Text
                                    required
                                    label="Complemento:"
                                    name="address_complement"
                                    value={form?.address_complement ?? ''}
                                    error={errors?.address_complement}
                                    onChange={onChange}
                                    isDisabled={loading}
                                />
                            </HStack>
                        </>
                    )}
                </Stack>
            </ModalBody>
            <ModalFooter>
                <ButtonGroup size="sm" isDisabled={loading}>
                    <Button
                        isDisabled={!showNucleusForm || !form.name || existingDistributor}
                        leftIcon={<Icon name="distributor" />}
                        onClick={auth.onOpen}
                    >
                        Criar núcleo
                    </Button>
                    <Button colorScheme="red" onClick={props.onClose}>Cancelar</Button>
                </ButtonGroup>
            </ModalFooter>
        </Modal>

        <Authenticate {...auth} onSuccess={handleSubmit} />
        </>
    )
}
